import styled from '@emotion/styled';
import { useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  buttonIds,
  gtmEvents,
  responseTypes, routesPaths,
  socialType
} from '@selfserve/react-utils';
import {
  storage,
  storageKeys,
  useLog,
  usePrevious,
  useQueryParams,
  useStorage,
} from '@selfserve/react-hooks';
import { IWrappedComponentProps } from '@selfserve/react-hocs';

import { useSocialLoginRequest } from '../data';

const NX_GOOGLE_CLIENT_ID = process.env['NX_GOOGLE_CLIENT_ID'];
const NX_FACEBOOK_APP_ID = process.env['NX_FACEBOOK_APP_ID'];
const ALREADY_REGISTERED_ERROR_ID = 10019;

const StyledContainer = styled('div')(({ theme }: { theme?: any }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledButtons = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  gridGap: theme.spacing(3),
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const StyledButton = styled('div')(({ theme, disabled }: { theme?: any, disabled?: boolean }) => ({
  border: `1px solid ${theme.palette.grey['400']}`,
  borderRadius: '4px',
  height: '40px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...(!disabled && {
    '&:hover': {
      backgroundColor: theme.palette.grey['600'],
    },
  }),
  ...(disabled && {
    pointerEvents: 'none',
    cursor: 'default',
  }),
}));

const StyledImage = styled('div')(
  ({ theme, src }: { theme?: any; src?: string }) => ({
    borderRight: `1px solid ${theme.palette.grey['400']}`,
    width: '40px',
    height: '40px',
    background: `url("${src}") no-repeat center`,
    pointerEvents: 'none',
  })
);

const StyledName = styled('div')(({ theme }: { theme?: any }) => ({
  width: '100%',
  textAlign: 'center',
  color: theme.palette.grey['100'],
  fontFamily: 'Centra No1',
  fontWeight: '500',
  pointerEvents: 'none',
  lineHeight: '21px',
  marginRight: '4px',
  marginLeft: '4px',
}));

const StyledDivider = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledDividerLine = styled('div')(({ theme }: { theme?: any }) => ({
  flex: 1,
  background: theme.palette.grey['400'],
  height: '1px',
}));

const StyledDividerText = styled('div')(({ theme }: { theme?: any }) => ({
  textTransform: 'uppercase',
  fontSize: '16px',
  fontFamily: 'Centra No1',
  fontWeight: '500',
  padding: '0 7px',
  color: theme.palette.grey['300'],
}));

type Props = IWrappedComponentProps & {
  setFieldError: (field: string, message: string) => void;
  onLoadingChange: (isLoading: boolean) => void;
  loading: boolean;
};

export const SocialLogin = ({ openErrorModal, setFieldError, onLoadingChange, loading }: Props) => {
  const { t } = useTranslation();
  const { log } = useLog();
  const { desiredPackageId } = useParams();
  const queryParams = useQueryParams();
  const [currentExistingUser, setExistingUser] = useStorage(
    storage.LOCAL,
    storageKeys.EXISTING_USER,
    false
  );
  const { mutateAsync: handleSocialLoginRequest, variables, isLoading } =
    useSocialLoginRequest();
  const googleButton = useRef<HTMLDivElement>();
  const prevGoogleButton = usePrevious<MutableRefObject<HTMLDivElement>>(
    googleButton as MutableRefObject<HTMLDivElement>
  );

  const responseHandle = useCallback(
    (email: string, status?: number, url?: string, message?: string, existingUser?: boolean, id?: number) => {
      if (String(status).startsWith(responseTypes.SUCCESS)) {
        if (existingUser) {
          setExistingUser(existingUser);
        } else if (currentExistingUser) {
          setExistingUser(false);
        }
        (window as any).dataLayer.push({
          event: gtmEvents.EMAIL_SUBMITION,
          email,
        });
        window.location.replace(url as string);
      } else if (message) {
        setFieldError('email', message);
        if (id === ALREADY_REGISTERED_ERROR_ID) {
          openErrorModal({
            url: routesPaths.LOGIN,
            text: t(`errors.${ALREADY_REGISTERED_ERROR_ID}`),
            confirmBtnText: t('common.login'),
            preventActionsOnClose: true,
            withDissmiss: true,
          });
        }
      } else {
        openErrorModal({ text: t(`oauth.${variables?.type}-error`) });
      }
    },
    [setFieldError, openErrorModal, variables, t]
  );

  const handleFacebookLogin = useCallback(() => {
    (window as any).FB.login(
      (response: any) => {
        if (response.authResponse) {
          (window as any).FB.api(
            '/me?fields=email',
            ({ email }: { email?: string } = {}) => {
              const { accessToken: token, userID: userId } =
                response.authResponse;
              handleSocialLoginRequest({
                ...queryParams,
                type: socialType.FACEBOOK,
                data: { token, userId },
                desiredPackageId,
              })
                .then(({ status, data }) =>
                  responseHandle(
                    email as string,
                    status,
                    data?.url,
                    data?.message_en,
                    data?.existingUser,
                    data?.id
                  )
                )
                .catch((error: Error) => {
                  log(error);
                  openErrorModal({
                    text: t(`oauth.${socialType.FACEBOOK}-error`),
                  });
                });
            }
          );
        }
      },
      { scope: 'email' }
    );
  }, [desiredPackageId, queryParams, handleSocialLoginRequest, openErrorModal]);

  useEffect(() => onLoadingChange(isLoading), [isLoading]);

  useEffect(() => {
    (window as any).FB.init({
      appId: NX_FACEBOOK_APP_ID,
      cookie: true, // Enable cookies to allow the server to access the session.
      xfbml: true, // Parse oauth plugins on this webpage.
      version: 'v13.0', // Use this Graph API version for this call.
    });
  }, []);

  useEffect(() => {
    if (!prevGoogleButton?.current && googleButton?.current) {
      (window as any).gapi.load('auth2', () => {
        // Retrieve the singleton for the GoogleAuth library and set up the client.
        const auth2 = (window as any).gapi.auth2.init({
          client_id: NX_GOOGLE_CLIENT_ID,
          cookiepolicy: 'none',
        });
        auth2.attachClickHandler(
          googleButton.current,
          {},
          (googleUser: any) => {
            const token = googleUser.getAuthResponse().id_token;
            const email = googleUser.getBasicProfile().getEmail();
            handleSocialLoginRequest({
              ...queryParams,
              type: socialType.GOOGLE,
              data: { token },
              desiredPackageId,
            })
              .then(({ status, data }) =>
                responseHandle(
                  email,
                  status,
                  data?.url,
                  data?.message_en,
                  data?.existingUser,
                  data?.id
                )
              )
              .catch((error: Error) => {
                log(error);
                openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
              });
          },
          (error: Error) => {
            log(error);
            openErrorModal({ text: t(`oauth.${socialType.GOOGLE}-error`) });
          }
        );
      });
    }
  }, [googleButton, prevGoogleButton]);

  return (
    <StyledContainer className="oauth-block">
      <StyledButtons className="oauth-buttons">
        <StyledButton
          ref={googleButton as MutableRefObject<HTMLDivElement>}
          className="oauth-button"
          id={buttonIds.googleRegistration}
          disabled={loading}
        >
          <StyledImage
            className={`oauth-icon ${socialType.GOOGLE}`}
            src={`assets/${socialType.GOOGLE}.svg`}
          />
          <StyledName className="oauth-button-text">
            {t(`oauth.${socialType.GOOGLE}`)}
          </StyledName>
        </StyledButton>
        <StyledButton
          onClick={handleFacebookLogin}
          className="oauth-button"
          id={buttonIds.facebookRegistration}
          disabled={loading}
        >
          <StyledImage
            className={`oauth-icon ${socialType.FACEBOOK}`}
            src={`assets/${socialType.FACEBOOK}.svg`}
          />
          <StyledName className="oauth-button-text">
            {t(`oauth.${socialType.FACEBOOK}`)}
          </StyledName>
        </StyledButton>
      </StyledButtons>
      <StyledDivider className="divider">
        <StyledDividerLine className="divider-line" />
        <StyledDividerText className="divider-or">
          {t('get-started.or-sign-up-with')}
        </StyledDividerText>
        <StyledDividerLine className="divider-line" />
      </StyledDivider>
    </StyledContainer>
  );
};
